// src/screens/AuthFlow.jsx
import { useState } from "react";
import { C, F, card, tx } from "../constants";
import { Icon, Input, PrimaryBtn } from "../components/UI";
import { useAuth } from "../context/AuthContext";

// ── Shared wave background ────────────────────────────────────────────────────
function AuthBg({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: C.bg, position: "relative", overflow: "hidden", fontFamily: F }}>
      {/* Decorative ocean gradient orbs */}
      <div style={{ position: "absolute", top: -80, right: -80, width: 320, height: 320, borderRadius: "50%", background: `radial-gradient(circle, ${C.primary}15 0%, transparent 70%)`, pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: 40, left: -60, width: 260, height: 260, borderRadius: "50%", background: `radial-gradient(circle, ${C.green}10 0%, transparent 70%)`, pointerEvents: "none" }} />
      {children}
    </div>
  );
}

// ── Auth Screen ───────────────────────────────────────────────────────────────
export function AuthScreen({ lang, onSuccess, initialView = "land", onBackToPitch }) {
  const { register, login } = useAuth();
  // land | log | reg. The landing page sends people straight to the one they
  // asked for, so "land" is now only reached by backing out of the other two.
  const [view,    setView]    = useState(initialView);
  const [name,    setName]    = useState("");
  const [email,   setEmail]   = useState("");
  const [pass,    setPass]    = useState("");
  const [conf,    setConf]    = useState("");
  const [agreed,  setAgreed]  = useState(false);
  const [errs,    setErrs]    = useState({});
  const [loading, setLoading] = useState(false);
  const [apiErr,  setApiErr]  = useState("");
  const [showPw,  setShowPw]  = useState(false);

  const t = (key) => tx(lang, key);

  const strength = pass.length === 0 ? 0 : pass.length < 6 ? 1 : pass.length < 10 ? 2 : /[A-Z]/.test(pass) && /[0-9]/.test(pass) ? 4 : 3;
  const strLabel = ["","Weak","Fair","Good","Strong"];
  const strColor = ["",C.coral,C.gold,C.primary,C.green];

  const doLogin = async () => {
    const e = {};
    if (!email.trim()) e.email = "Enter the email address you signed up with.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = "That does not look like an email address — check for a missing @ or a typo.";
    if (!pass) e.pass = "Enter your password.";
    if (Object.keys(e).length) { setErrs(e); return; }
    setLoading(true); setApiErr("");
    try {
      await login({ email, password: pass });
      onSuccess();
    } catch (err) {
      console.error("Sign-in failed:", err);
      const msg = err.code === "auth/invalid-credential" || err.code === "auth/wrong-password"
        ? "That email and password do not match an account on this phone."
        : err.code === "auth/user-not-found"
        ? "No account on this phone uses that email. Create one below."
        : err.code === "auth/too-many-requests"
        ? "Too many attempts. Wait a few minutes and try again."
        : `Sign-in did not work: ${err?.message || "unknown error"}.`;
      setApiErr(msg);
    }
    setLoading(false);
  };

  const doRegister = async () => {
    const e = {};
    if (name.trim().length < 2) e.name = "Enter your name as coordinators will know you — it appears next to every report you file.";
    if (!email.trim()) e.email = "Enter an email address. It is how a coordinator reaches you about a reward or a report.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = "That does not look like an email address — check for a missing @ or a typo.";
    if (pass.length < 6) e.pass = `Use at least 6 characters — yours has ${pass.length}.`;
    if (!conf) e.conf = "Type the password a second time so we know it is what you meant.";
    else if (pass !== conf) e.conf = "The two passwords are different. Check for a typo in one of them.";
    if (!agreed) e.terms = "Tick the box to confirm you agree before creating the account.";
    if (Object.keys(e).length) { setErrs(e); return; }
    setLoading(true); setApiErr("");
    try {
      await register({ name: name.trim(), email, password: pass, selectedLang: lang });
      onSuccess();
    } catch (err) {
      const msg = err.message || "";
      console.error("Registration failed:", err);
      const apiMsg = (err.code === "auth/email-already-in-use" || msg.toLowerCase().includes("already"))
        ? "An account on this phone already uses that email. Sign in instead."
        : (err.code === "auth/weak-password" || msg.toLowerCase().includes("6 char"))
        ? "That password is too short — use at least 6 characters."
        : `The account could not be created: ${msg || "unknown error"}.`;
      setApiErr(apiMsg);
    }
    setLoading(false);
  };

  // ── Landing screen ──────────────────────────────────────────────────────────
  if (view === "land") {
    return (
      <AuthBg>
        <div style={{ padding: "56px 24px 0", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 9, marginBottom: 6 }}>
            <Icon name="ecg_heart" size={26} color={C.primary} />
            <span style={{ fontFamily: F, fontSize: 26, fontWeight: 800, color: C.text, letterSpacing: "-0.5px" }}>CoastClear</span>
          </div>
          <div style={{ fontSize: 11, color: C.primary, letterSpacing: "1.2px", textTransform: "uppercase", fontWeight: 700 }}>
            {t("tagline")}
          </div>
        </div>
        <div style={{ padding: "0 24px 40px", fontFamily: F }}>
          <div style={{ textAlign: "center", margin: "30px 0 28px" }}>
            <h1 style={{ fontSize: 25, fontWeight: 800, color: C.text, marginBottom: 8, letterSpacing: "-0.5px", lineHeight: 1.25 }}>{t("landTitle")}</h1>
            <p style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.6 }}>{t("landSub")}</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <PrimaryBtn onClick={() => setView("reg")} color={C.primary}>
              <Icon name="person_add" size={18} color={C.bg} />
              Create an account
            </PrimaryBtn>
            <button onClick={() => setView("log")} style={{ width: "100%", padding: "15px", background: "transparent", border: `1.5px solid ${C.border}`, borderRadius: 99, fontFamily: F, fontSize: 15, fontWeight: 600, color: C.text, cursor: "pointer" }}>
              {t("signIn")}
            </button>
          </div>
          <p style={{ textAlign: "center", fontSize: 11, color: C.dim, marginTop: 20, lineHeight: 1.5 }}>
            By continuing you agree to the terms of service and privacy policy.
          </p>
        </div>
      </AuthBg>
    );
  }

  // ── Login ───────────────────────────────────────────────────────────────────
  if (view === "log") {
    return (
      <AuthBg>
        <div style={{ padding: "56px 24px 40px", fontFamily: F }}>
          <button onClick={() => (onBackToPitch ? onBackToPitch() : setView("land"))} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: C.muted, fontSize: 13, marginBottom: 28 }}>
            <Icon name="arrow_back" size={18} color={C.muted} />
            Back
          </button>

          <div style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: C.text, marginBottom: 6, letterSpacing: "-0.5px" }}>{t("signIn")}</h2>
            <p style={{ fontSize: 13, color: C.muted }}>{t("signInSub")}</p>
          </div>

          {apiErr && (
            <div style={{ background: `${C.coral}15`, border: `1px solid ${C.coral}35`, borderRadius: 12, padding: "12px 14px", fontSize: 13, color: C.coral, marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
              <Icon name="error" size={16} color={C.coral} />
              {apiErr}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 20 }}>
            <Input label={t("email")} value={email} onChange={setEmail} type="email" placeholder="you@example.com" error={errs.email} icon="mail" />
            <Input label={t("password")} value={pass} onChange={setPass} type={showPw ? "text" : "password"} placeholder="••••••••" error={errs.pass} icon="lock"
              rightEl={<button onClick={() => setShowPw(v => !v)} style={{ background: "none", border: "none", cursor: "pointer" }}><Icon name={showPw ? "visibility_off" : "visibility"} size={18} color={C.muted} /></button>}
            />
          </div>

          <p style={{ fontSize: 11.5, color: C.dim, lineHeight: 1.5, marginBottom: 20 }}>
            Forgotten your password? Accounts are held on this phone only, so it cannot be reset from
            here — ask your ICENECDEV coordinator to set you up again.
          </p>

          <PrimaryBtn onClick={doLogin} loading={loading}>
            <Icon name="login" size={18} color={C.bg} />
            {t("signIn")}
          </PrimaryBtn>

          <p style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: C.muted }}>
            {t("noAccount")}{" "}
            <button onClick={() => { setView("reg"); setErrs({}); setApiErr(""); }} style={{ background: "none", border: "none", color: C.primary, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
              {t("createAcc")}
            </button>
          </p>
        </div>
      </AuthBg>
    );
  }

  // ── Register ────────────────────────────────────────────────────────────────
  return (
    <AuthBg>
      <div style={{ padding: "56px 24px 60px", fontFamily: F }}>
        <button onClick={() => (onBackToPitch ? onBackToPitch() : setView("land"))} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: C.muted, fontSize: 13, marginBottom: 28 }}>
          <Icon name="arrow_back" size={18} color={C.muted} />
          Back
        </button>

        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: C.text, marginBottom: 6, letterSpacing: "-0.5px" }}>{t("createAcc")}</h2>
          <p style={{ fontSize: 13, color: C.muted }}>{t("registerSub")}</p>
        </div>

        {apiErr && (
          <div style={{ background: `${C.coral}15`, border: `1px solid ${C.coral}35`, borderRadius: 12, padding: "12px 14px", fontSize: 13, color: C.coral, marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
            <Icon name="error" size={16} color={C.coral} />
            {apiErr}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
          <Input label={t("name")} value={name} onChange={setName} placeholder="Jean-Pierre Mbarga" error={errs.name} icon="badge" />
          <Input label={t("email")} value={email} onChange={setEmail} type="email" placeholder="you@example.com" error={errs.email} icon="mail" />
          <div>
            <Input label={t("password")} value={pass} onChange={setPass} type={showPw ? "text" : "password"} placeholder="••••••••" error={errs.pass} icon="lock"
              rightEl={<button onClick={() => setShowPw(v => !v)} style={{ background: "none", border: "none", cursor: "pointer" }}><Icon name={showPw ? "visibility_off" : "visibility"} size={18} color={C.muted} /></button>}
            />
            {pass.length > 0 && (
              <div style={{ marginTop: 6 }}>
                <div style={{ height: 4, background: C.faint, borderRadius: 99, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${(strength / 4) * 100}%`, background: strColor[strength], borderRadius: 99, transition: "width .3s, background .3s" }} />
                </div>
                <div style={{ fontSize: 10.5, color: strColor[strength], marginTop: 4, textAlign: "right" }} aria-live="polite">
                  Password strength: {strLabel[strength]}
                </div>
              </div>
            )}
          </div>
          <Input label={t("confirmPw")} value={conf} onChange={setConf} type="password" placeholder="••••••••" error={errs.conf} icon="lock_reset" />
        </div>

        {/* Terms */}
        <div style={{ marginBottom: 20 }}>
          <div
            role="checkbox"
            aria-checked={agreed}
            tabIndex={0}
            onClick={() => setAgreed(a => !a)}
            onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); setAgreed(a => !a); } }}
            style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}
          >
            <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${agreed ? C.primary : C.border}`, background: agreed ? `${C.primary}25` : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1, transition: "all .2s" }}>
              {agreed && <Icon name="check" size={13} color={C.primary} />}
            </div>
            <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              I agree to the <span style={{ color: C.primary }}>terms of service</span>. My reports may be shared with ICENECDEV coordinators and public health partners.
            </span>
          </div>
          {errs.terms && (
            <div role="alert" style={{ fontSize: 11.5, color: C.coral, marginTop: 6, display: "flex", gap: 5, alignItems: "flex-start", lineHeight: 1.4 }}>
              <Icon name="error" size={13} color={C.coral} style={{ flexShrink: 0, marginTop: 1 }} />
              {errs.terms}
            </div>
          )}
        </div>

        <PrimaryBtn onClick={doRegister} loading={loading} disabled={!agreed}>
          <Icon name="how_to_reg" size={18} color={C.bg} />
          {t("createAcc")}
        </PrimaryBtn>

        <p style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: C.muted }}>
          {t("hasAccount")}{" "}
          <button onClick={() => { setView("log"); setErrs({}); setApiErr(""); }} style={{ background: "none", border: "none", color: C.primary, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
            {t("signIn")}
          </button>
        </p>
      </div>
    </AuthBg>
  );
}

// ── Role Selection ────────────────────────────────────────────────────────────
export function RoleScreen() {
  const [sel, setSel] = useState(null);
  const [saving, setSaving] = useState(false);
  const { updateRole, lang } = useAuth();

  const roles = [
    {
      id: "reporter", matIcon: "photo_camera", color: C.primary,
      title: "Community reporter",
      desc: "You spot problems where you live and log them. Most volunteers start here.",
      perks: ["Report hazards and waste", "Run household surveys", "Earn points"],
    },
    {
      id: "cleanup", matIcon: "cleaning_services", color: C.green,
      title: "Cleanup crew",
      desc: "You turn up and clear the sites others report. Before and after photos are required.",
      perks: ["Claim missions", "Everything a reporter can do", "Bonus points on completion"],
    },
    {
      id: "ngo", matIcon: "verified_user", color: C.gold,
      title: "Coordinator",
      desc: "For ICENECDEV staff. You assign responses, review incoming reports and export the dataset.",
      perks: ["Impact dashboard", "Assign and close responses", "CSV and Excel export"],
    },
  ];

  const r = roles.find(x => x.id === sel);

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: F, display: "flex", flexDirection: "column" }}>
      {/* Header image */}
      <div style={{ height: 160, position: "relative", overflow: "hidden" }}>
        <img src="https://images.unsplash.com/photo-1574169208507-843761246aa5?w=800&q=80" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to bottom, rgba(16,36,46,0.22), ${C.bg})` }} />
      </div>

      <div style={{ flex: 1, padding: "0 20px 100px" }}>
        <div style={{ textAlign: "center", marginBottom: 24, marginTop: -10, position: "relative", zIndex: 1 }}>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: C.text, marginBottom: 6, letterSpacing: "-0.5px" }}>What will you be doing?</h1>
          <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.55 }}>This sets which screens you see. A coordinator can change it later.</p>
        </div>

        <div role="radiogroup" aria-label="Choose your role" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {roles.map((role, i) => (
            <div
              key={role.id}
              role="radio"
              aria-checked={sel === role.id}
              tabIndex={0}
              onClick={() => setSel(role.id)}
              onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); setSel(role.id); } }}
              style={{ background: sel === role.id ? `${role.color}10` : C.card, border: `2px solid ${sel === role.id ? role.color : C.border}`, borderRadius: 18, padding: 16, cursor: "pointer", transition: "border-color .25s, background .25s", animation: `fadeUp .5s ease ${i * 0.08}s both` }}
            >
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: sel === role.id ? `${role.color}25` : C.faint, border: `1.5px solid ${sel === role.id ? role.color + "50" : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .2s" }}>
                  <Icon name={role.matIcon} size={24} color={sel === role.id ? role.color : C.muted} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: sel === role.id ? role.color : C.text }}>{role.title}</span>
                    <div style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${sel === role.id ? role.color : C.border}`, background: sel === role.id ? role.color : "transparent", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .2s" }}>
                      {sel === role.id && <Icon name="check" size={12} color={C.bg} />}
                    </div>
                  </div>
                  <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: sel === role.id ? 10 : 0 }}>{role.desc}</p>
                  {sel === role.id && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {role.perks.map(p => (
                        <span key={p} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: `${role.color}18`, border: `1px solid ${role.color}3d`, borderRadius: 99, padding: "3px 10px", fontSize: 11, color: role.color, fontWeight: 600 }}>
                          <Icon name="check" size={11} color={role.color} />
                          {p}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sticky confirm button */}
      <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 480, padding: "16px 20px 28px", background: `linear-gradient(to top, ${C.bg} 70%, transparent)` }}>
        <PrimaryBtn
          onClick={async () => { if (!sel) return; setSaving(true); await updateRole(sel); setSaving(false); }}
          disabled={!sel} loading={saving} color={r?.color || C.primary}
        >
          {sel ? `Continue as ${r?.title?.toLowerCase()}` : "Choose one to continue"}
          {sel && <Icon name="arrow_forward" size={18} color={C.bg} />}
        </PrimaryBtn>
      </div>
    </div>
  );
}
